import { Flex } from 'antd';
import { useMemo, useState } from 'react';

import { TABLE_SETTINGS_ACTION_ID } from './buildTableSettingsAction';
import {
  FilteredListingFilterSidebar,
  FilteredListingHeaderFilters,
} from './FilteredListingFilterSidebar';
import { FilteredListingFilterSidebarToggle } from './FilteredListingFilterSidebarToggle';
import {
  FilteredListingPageHeader,
  type FilteredListingHeaderActionItem,
  type FilteredListingPageHeaderProps,
} from './FilteredListingPageHeader';
import { FilteredListingResponsiveFilterLayout } from './FilteredListingResponsiveFilterLayout';
import { FilteredListingShell } from './FilteredListingShell';
import {
  FilteredListingToolbar,
  type FilteredListingToolbarProps,
} from './FilteredListingToolbar';

import type { FilterGroup } from './filteredListingFilterGroups';
import type { FilteredListingSearchRowProps } from './FilteredListingSearchRow';
import type { SmartPaginationProps } from './SmartPagination';
import type { EmptyListingConfig } from '@/UI/Empty/Empty';
import type React from 'react';

const SETTINGS_LIKE_ACTION_IDS = new Set([
  TABLE_SETTINGS_ACTION_ID,
  'visible-stages',
]);

type FilteredListingGridMode = 'sidebar' | 'fullwidth';

const FILTER_SIDEBAR_TOGGLE_ACTION_ID = 'filter-sidebar-toggle';

/** Places the sidebar toggle right before table/settings-like controls, else at the end. */
function withFilterSidebarToggle(
  items: FilteredListingHeaderActionItem[] | undefined,
  toggle: React.ReactNode,
): FilteredListingHeaderActionItem[] {
  const toggleItem: FilteredListingHeaderActionItem = {
    id: FILTER_SIDEBAR_TOGGLE_ACTION_ID,
    kind: 'custom',
    render: () => toggle,
  };
  const current = items ?? [];
  const tableSettingsIndex = current.findIndex((item) =>
    SETTINGS_LIKE_ACTION_IDS.has(item.id),
  );
  if (tableSettingsIndex < 0) return [...current, toggleItem];
  return [
    ...current.slice(0, tableSettingsIndex),
    toggleItem,
    ...current.slice(tableSettingsIndex),
  ];
}

type FilteredListingGridProps = {
  'data-testid'?: string;
  mode?: FilteredListingGridMode;
  filtersActive: boolean;
  searchHasValue?: boolean;
  onClearAllFilters?: () => void;
  search?: FilteredListingSearchRowProps;
  topFilters?: React.ReactNode;
  filterGroups: FilterGroup[];
  header?: FilteredListingPageHeaderProps;
  toolbar?: FilteredListingToolbarProps;
  pagination?: SmartPaginationProps | false | null;
  loading?: boolean;
  resultCount?: number;
  empty?: EmptyListingConfig | null;
  emptyTestId?: string;
  initialLoadingPlaceholder?: React.ReactNode;
  children: React.ReactNode;
};

export function FilteredListingGrid({
  'data-testid': dataTestId,
  mode = 'sidebar',
  filtersActive,
  searchHasValue = false,
  onClearAllFilters,
  search,
  topFilters,
  filterGroups,
  header,
  toolbar,
  pagination,
  loading = false,
  resultCount,
  empty,
  emptyTestId,
  initialLoadingPlaceholder,
  children,
}: FilteredListingGridProps) {
  const [filterSidebarCollapsed, setFilterSidebarCollapsed] = useState(false);
  const clearFiltersActive = filtersActive || searchHasValue;
  const sidebarShared = useMemo(
    () => ({
      search,
      topFilters,
      filterGroups,
      filtersActive: clearFiltersActive,
      onClearAllFilters,
    }),
    [search, topFilters, filterGroups, clearFiltersActive, onClearAllFilters],
  );

  const showEmptyClear = Boolean(
    empty?.clearLabel && onClearAllFilters && (filtersActive || searchHasValue),
  );

  const isFullwidth = mode === 'fullwidth';
  const hasHeaderFilters =
    search != null ||
    (topFilters != null && topFilters !== false) ||
    filterGroups.length > 0;

  const filterSidebarToggle = isFullwidth ? null : (
    <FilteredListingFilterSidebarToggle
      collapsed={filterSidebarCollapsed}
      onToggle={() => setFilterSidebarCollapsed((current) => !current)}
    />
  );

  const shell = (
    <FilteredListingShell
      header={
        header ? (
          <FilteredListingPageHeader
            {...header}
            actionItems={
              filterSidebarToggle
                ? withFilterSidebarToggle(
                    header.actionItems,
                    filterSidebarToggle,
                  )
                : header.actionItems
            }
            actionPrefix={
              isFullwidth && hasHeaderFilters ? (
                <FilteredListingHeaderFilters {...sidebarShared} />
              ) : undefined
            }
          />
        ) : filterSidebarToggle ? (
          <Flex justify="flex-end">{filterSidebarToggle}</Flex>
        ) : null
      }
      secondaryToolbar={
        toolbar ? <FilteredListingToolbar {...toolbar} /> : null
      }
      pagination={pagination}
      loading={loading}
      resultCount={resultCount}
      empty={empty}
      showEmptyClear={showEmptyClear}
      onClearAllFilters={onClearAllFilters}
      emptyTestId={emptyTestId}
      initialLoadingPlaceholder={initialLoadingPlaceholder}
    >
      {children}
    </FilteredListingShell>
  );

  return (
    <Flex data-testid={dataTestId ?? 'filtered-listing-grid'} vertical>
      {isFullwidth ? (
        shell
      ) : (
        <FilteredListingResponsiveFilterLayout
          inlineFilters={<FilteredListingFilterSidebar {...sidebarShared} />}
          collapsed={filterSidebarCollapsed}
          onCollapsedChange={setFilterSidebarCollapsed}
        >
          {shell}
        </FilteredListingResponsiveFilterLayout>
      )}
    </Flex>
  );
}
